/**
 * Profile Page Component
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Shield, Settings, Bell, Sun, Moon, MapPin, LogOut, Loader2, LifeBuoy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { API_URL } from '../config';
import ContactSupportButtons from '../components/ContactSupportButtons';

const ProfilePage = () => {
  const { user, updateUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });
  const [loading, setLoading] = useState(false);
  const [showChangePIN, setShowChangePIN] = useState(false);
  const [pinData, setPinData] = useState({ newPin: "", confirmPin: "" });
  const [pinLoading, setPinLoading] = useState(false);
  const [settings, setSettings] = useState({
    language: localStorage.getItem("app_language") || "en",
    emailNotifications: true,
    biometricEnabled: localStorage.getItem("biometric_enabled") === "true"
  });
  const [biometricSupported, setBiometricSupported] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        email: user.email || "",
        phone: user.phone || "",
      });
    }
  }, [user]);

  useEffect(() => {
    if (window.PublicKeyCredential) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(available => setBiometricSupported(available))
        .catch(() => setBiometricSupported(false));
    }
    loadNotificationSettings();
  }, []);

  const loadNotificationSettings = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API_URL}/notifications/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, emailNotifications: response.data.payment_notifications ?? true }));
    } catch (error) {
      console.error("Failed to load settings:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const params = new URLSearchParams();
      if (formData.full_name) params.append("full_name", formData.full_name);
      if (formData.email) params.append("email", formData.email);
      if (formData.phone) params.append("phone", formData.phone);
      const response = await axios.put(`${API_URL}/auth/profile?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser(response.data);
      toast.success("Profile updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePIN = async (e) => {
    e.preventDefault();
    if (pinData.newPin.length < 4 || pinData.newPin.length > 6) { toast.error("PIN must be 4-6 digits"); return; }
    if (pinData.newPin !== pinData.confirmPin) { toast.error("PINs do not match"); return; }
    if (!/^\d+$/.test(pinData.newPin)) { toast.error("PIN must contain only numbers"); return; }

    setPinLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(`${API_URL}/auth/set-pin`, { pin: pinData.newPin }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("PIN changed successfully!");
      setShowChangePIN(false);
      setPinData({ newPin: "", confirmPin: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change PIN");
    } finally {
      setPinLoading(false);
    }
  };

  const handleLanguageChange = (lang) => {
    setSettings(prev => ({ ...prev, language: lang }));
    localStorage.setItem("app_language", lang);
    toast.success(lang === "en" ? "Language set to English" : lang === "ny" ? "Chilankhulo chasinthidwa ku Chichewa" : "Sprache auf Deutsch gesetzt");
    window.location.reload();
  };

  const handleBiometricToggle = async () => {
    if (!biometricSupported) { toast.error("Biometric authentication not supported"); return; }

    if (!settings.biometricEnabled) {
      try {
        const challenge = new Uint8Array(32);
        crypto.getRandomValues(challenge);
        
        const credential = await navigator.credentials.create({
          publicKey: {
            challenge,
            rp: { name: "SmartPay Utilities", id: window.location.hostname },
            user: { id: new TextEncoder().encode(user.id), name: user.phone, displayName: user.full_name || user.phone },
            pubKeyCredParams: [{ alg: -7, type: "public-key" }, { alg: -257, type: "public-key" }],
            authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required" },
            timeout: 60000
          }
        });

        if (credential) {
          localStorage.setItem("biometric_enabled", "true");
          localStorage.setItem("biometric_credential_id", btoa(String.fromCharCode(...new Uint8Array(credential.rawId))));
          setSettings(prev => ({ ...prev, biometricEnabled: true }));
          toast.success("Biometric authentication enabled!");
        }
      } catch (error) {
        toast.error("Failed to enable biometric authentication");
      }
    } else {
      localStorage.removeItem("biometric_enabled");
      localStorage.removeItem("biometric_credential_id");
      setSettings(prev => ({ ...prev, biometricEnabled: false }));
      toast.success("Biometric authentication disabled");
    }
  };

  const handleEmailNotificationToggle = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const newValue = !settings.emailNotifications;
      await axios.put(`${API_URL}/notifications/settings?payment_notifications=${newValue}&expiry_notifications=${newValue}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, emailNotifications: newValue }));
      toast.success(newValue ? "Email notifications enabled" : "Email notifications disabled");
    } catch (error) {
      toast.error("Failed to update notification settings");
    }
  };

  const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "ny", name: "Chichewa", flag: "🇲🇼" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" }
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Your profile</h1>
        <p className="text-sm text-muted-foreground">How we greet you, and how you like the app to feel.</p>
      </div>

      {/* User Info Card */}
      <div className="bg-card border border-border/50 rounded-2xl p-6">
        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border/50">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
            {user?.is_admin ? <Shield className="w-8 h-8 text-primary" /> : <User className="w-8 h-8 text-primary" />}
          </div>
          <div>
            <p className="font-semibold text-lg">{user?.full_name || "User"}</p>
            <p className="text-muted-foreground">{user?.phone || user?.email || "No phone added yet"}</p>
            {user?.is_admin && <span className="inline-block mt-1 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">Admin</span>}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Full Name</label>
            <input type="text" value={formData.full_name} onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
              placeholder="Enter your full name" className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="Enter your email" className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Phone Number</label>
            <input type="tel" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="0994123079" className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
            <p className="text-xs text-muted-foreground mt-1">Add your phone to log in with phone + PIN (SMS not required until Twilio is live).</p>
          </div>
          <button type="submit" disabled={loading} className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update Profile"}
          </button>
        </form>
      </div>

      {/* Security Settings */}
      <div className="bg-card border border-border/50 rounded-2xl p-6">
        <h2 className="font-semibold mb-4 flex items-center gap-2"><Shield className="w-5 h-5 text-primary" /> Security</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center"><Settings className="w-5 h-5 text-blue-500" /></div>
              <div><p className="font-medium">Change PIN</p><p className="text-sm text-muted-foreground">Update your login PIN</p></div>
            </div>
            <button onClick={() => setShowChangePIN(!showChangePIN)} className="px-4 py-2 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary/20 transition-all" data-testid="change-pin-btn">
              {showChangePIN ? "Cancel" : "Change"}
            </button>
          </div>

          {showChangePIN && (
            <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} onSubmit={handleChangePIN} className="space-y-3 pt-2">
              <input type="password" inputMode="numeric" maxLength={6} value={pinData.newPin} onChange={(e) => setPinData(prev => ({ ...prev, newPin: e.target.value.replace(/\D/g, "") }))}
                placeholder="New PIN (4-6 digits)" className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl text-center tracking-widest" data-testid="new-pin-input" />
              <input type="password" inputMode="numeric" maxLength={6} value={pinData.confirmPin} onChange={(e) => setPinData(prev => ({ ...prev, confirmPin: e.target.value.replace(/\D/g, "") }))}
                placeholder="Confirm new PIN" className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl text-center tracking-widest" data-testid="confirm-pin-input" />
              <button type="submit" disabled={pinLoading} className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-pin-btn">
                {pinLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save New PIN"}
              </button>
            </motion.form>
          )}

          <div className="flex items-center justify-between py-3 border-b border-border/30">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${biometricSupported ? "bg-emerald-500/20" : "bg-gray-500/20"}`}>
                <Shield className={`w-5 h-5 ${biometricSupported ? "text-emerald-500" : "text-gray-500"}`} />
              </div>
              <div><p className="font-medium">Biometric Login</p><p className="text-sm text-muted-foreground">{biometricSupported ? "Use fingerprint or face" : "Not supported"}</p></div>
            </div>
            <button onClick={handleBiometricToggle} disabled={!biometricSupported}
              className={`relative w-12 h-6 rounded-full transition-all ${settings.biometricEnabled ? "bg-emerald-500" : "bg-muted"} ${!biometricSupported && "opacity-50 cursor-not-allowed"}`} data-testid="biometric-toggle">
              <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${settings.biometricEnabled ? "left-6" : "left-0.5"}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-card border border-border/50 rounded-2xl p-6">
        <h2 className="font-semibold mb-4 flex items-center gap-2"><Bell className="w-5 h-5 text-primary" /> Notifications</h2>
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center"><Bell className="w-5 h-5 text-orange-500" /></div>
            <div><p className="font-medium">Email Notifications</p><p className="text-sm text-muted-foreground">Payment & subscription alerts</p></div>
          </div>
          <button onClick={handleEmailNotificationToggle}
            className={`relative w-12 h-6 rounded-full transition-all ${settings.emailNotifications ? "bg-emerald-500" : "bg-muted"}`} data-testid="email-notif-toggle">
            <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${settings.emailNotifications ? "left-6" : "left-0.5"}`} />
          </button>
        </div>
      </div>

      {/* Appearance */}
      <div className="bg-card border border-border/50 rounded-2xl p-6">
        <h2 className="font-semibold mb-4 flex items-center gap-2"><Sun className="w-5 h-5 text-primary" /> Appearance</h2>
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === "dark" ? "bg-indigo-500/20" : "bg-yellow-500/20"}`}>
              {theme === "dark" ? <Moon className="w-5 h-5 text-indigo-500" /> : <Sun className="w-5 h-5 text-yellow-500" />}
            </div>
            <div><p className="font-medium">Dark Mode</p><p className="text-sm text-muted-foreground">{theme === "dark" ? "Dark theme" : "Light theme"}</p></div>
          </div>
          <button onClick={toggleTheme}
            className={`relative w-12 h-6 rounded-full transition-all ${theme === "dark" ? "bg-indigo-500" : "bg-muted"}`} data-testid="theme-toggle">
            <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${theme === "dark" ? "left-6" : "left-0.5"}`} />
          </button>
        </div>
      </div>

      {/* Language */}
      <div className="bg-card border border-border/50 rounded-2xl p-6">
        <h2 className="font-semibold mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /> Language</h2>
        <div className="grid grid-cols-3 gap-3">
          {languages.map((lang) => (
            <button key={lang.code} onClick={() => handleLanguageChange(lang.code)}
              className={`p-4 rounded-xl border transition-all flex flex-col items-center gap-2 ${settings.language === lang.code ? "bg-primary/10 border-primary" : "bg-card border-border hover:border-primary/50"}`} data-testid={`lang-${lang.code}`}>
              <span className="text-2xl">{lang.flag}</span>
              <span className={`text-sm font-medium ${settings.language === lang.code ? "text-primary" : ""}`}>{lang.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Help & Support */}
      <div className="bg-card border border-border/50 rounded-2xl p-6">
        <h2 className="font-semibold mb-4 flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-primary" /> Help &amp; Support</h2>
        <ContactSupportButtons />
      </div>

      {/* Logout */}
      <button onClick={logout} className="w-full py-4 bg-red-500/10 text-red-500 border border-red-500/30 rounded-xl font-semibold hover:bg-red-500/20 transition-all flex items-center justify-center gap-2" data-testid="logout-btn">
        <LogOut className="w-5 h-5" /> Logout
      </button>
    </motion.div>
  );
};

export default ProfilePage;